import { createColumnHelper } from '@tanstack/react-table';
import {
	ConfirmModal,
	createDataCollectionContext,
	dbTimeToUserTime,
	toaster,
	type ItemAction,
	type ConfirmModalHandle,
	parseErrorToString,
	type DataCollectionContextProps,
	useMobileContext,
	useValidatedSorting,
} from '@shared';
import ContactsService from '../../../../api/services/contactsService';
import { useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Box, Button, Card, Checkbox, Flex, Heading, Span, Text } from '@chakra-ui/react';
import { useDrawer } from '../../../../context/DrawerContext';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, useDeleteMultipleContacts } from '@admin';
import { IoBookOutline } from 'react-icons/io5';
import { useMeta } from '../../../../api/hooks/useMeta';
import { useGetAllGroups } from '@admin';
import { useGetAllCustomFields } from '@admin';
import AddToGroupsModal from './AddContactsModal';
import RemoveFromGroupsModal from './RemoveFromGroupsModal';
import contactsService from '../../../../api/services/contactsService';
import { DateTime } from 'luxon';
import ContactDrawer from './drawer/ContactDrawer';
import ChangeStatusModal from './ChangeStatusModal';
import { useNavigate } from 'react-router';

const columnHelper = createColumnHelper<Contact>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export const BrowseContacts = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { currentAccount } = useAuth();
	const { openDrawer, closeDrawer } = useDrawer();
	const { useDataCollection, DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<Contact>(),
		[],
	);
	const { isMobile } = useMobileContext();
	const queryClient = useQueryClient();
	const { meta } = useMeta('contactsService');
	const { data: groupData, isPending: isGroupsPending } = useGetAllGroups();
	const { data: customFieldData } = useGetAllCustomFields();

	function getBadgeColorByStatus(status: ContactStatus): string {
		switch (status) {
			case 'inactive':
				return 'yellow';
			case 'active':
				return 'green';
			case 'blocked':
				return 'red';
			default:
				return 'yellow';
		}
	}

	const columns = [
		columnHelper.accessor('contactID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('phone', {
			header: t('general.phone'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text userSelect={'text'}>{row.original.phone}</Text>;
			},
		}),
		columnHelper.accessor('status', {
			header: t('general.status'),
			sortingFn: 'basic',
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: [
					{ value: 'active', label: meta?.status?.['active'] ?? '' },
					{ value: 'inactive', label: meta?.status?.['inactive'] ?? '' },
					{ value: 'blocked', label: meta?.status?.['blocked'] ?? '' },
				] as Array<{ value: ContactStatus; label: string }>,
			},
			cell: ({ row }) => {
				return <Badge colorPalette={getBadgeColorByStatus(row.original.status)}>{meta?.status?.[row.original.status]}</Badge>;
			},
		}),
		columnHelper.accessor('firstName', {
			header: t('general.first_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('lastName', {
			header: t('general.last_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('email', {
			header: t('general.email'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('company', {
			header: t('general.company'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('jobTitle', {
			header: t('general.job_title'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('postalAddress', {
			header: t('general.postal_address'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('postalCode', {
			header: t('general.postal_code'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('city', {
			header: t('general.city'),
			sortingFn: 'basic',
		}),

		columnHelper.accessor(row => row.contactGroups, {
			id: 'contactGroups.groupID',
			header: t('general.groups'),
			enableSorting: false,
			filterFn: 'arrIncludesSome',
			meta: {
				filterVariant: 'select',
				filterOptions:
					groupData?.data.groups?.map(g => ({
						value: g.groupID.toString(),
						label: g.name,
					})) || [],
			},
			cell: ({ getValue }) => {
				const value = getValue();
				const groupIds = value?.map(v => v.groupID.toString());
				const valueText = groupData?.data.groups
					?.filter(g => groupIds?.includes(g.groupID.toString()))
					.map(gr => gr.name)
					.join(', ');
				return valueText ?? '';
			},
		}),

		...(customFieldData?.data?.customFields?.map(customField => {
			return columnHelper.accessor(row => row.contactCustomFields?.map(c => c.contactCustomFieldID), {
				id: `contactCustomFields.value:desc:customFieldID:${customField.customFieldID}`,
				header: customField.label,
				cell: ({ row }) => {
					return row.original.contactCustomFields?.find(contactCustomField => contactCustomField.customFieldID === customField.customFieldID)?.value || '-';
				},
			});
		}) || []),
		columnHelper.accessor('timeLatestMessageSent', {
			header: t('general.time_latest_message_sent'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeLatestMessageSent) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeLatestMessageSent, TIMEZONE);
			},
		}),
		columnHelper.accessor('createdFrom', {
			header: t('general.created_from'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.createdFrom === 'ui') {
					return t('general.from_ui');
				}
				return t('general.from_import');
			},
		}),
		columnHelper.accessor('timeUnsubscribed', {
			header: t('general.time_unsubscribed'),
			enableSorting: false,
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return dbTimeToUserTime(row.original.timeCreated, TIMEZONE, { includeSeconds: true });
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeModified, TIMEZONE);
			},
		}),
	];

	useValidatedSorting('contacts', columns);

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);
	const waitUserResponseToGroupBatch = useRef<ConfirmModalHandle>(null);

	const { mutate: deleteMultipleContacts } = useDeleteMultipleContacts();

	const selectedItemsActions: Array<ItemAction<Contact>> = [
		{
			id: 'sendMessage',
			label: t('general.send_message'),
			callbackFn: async ids => {
				navigate(`/messages?contactIDs=${ids.join(',')}`);
			},
		},
		{
			id: 'addToGroups',
			label: t('general.add_to_groups'),
			callbackFn: async ids => {
				if (!groupData) {
					return;
				}

				waitUserResponseToGroupBatch.current?.open({
					title: t('general.add_to_groups'),
					content: <AddToGroupsModal contactIDs={ids} groups={groupData.data.groups} ref={waitUserResponseToGroupBatch} />,
					isConfirmButtonVisible: false,
					isBackButtonVisible: false,
				});
			},
		},
		{
			id: 'removeFromGroups',
			label: t('general.remove_from_groups'),
			callbackFn: async ids => {
				if (!groupData) {
					return;
				}

				waitUserResponseToGroupBatch.current?.open({
					title: t('general.remove_from_groups'),
					content: <RemoveFromGroupsModal contactIDs={ids} groups={groupData.data.groups} ref={waitUserResponseToGroupBatch} />,
					isConfirmButtonVisible: false,
					isBackButtonVisible: false,
				});
			},
		},
		{
			id: 'bulkChangeStatus',
			label: t('general.change_status'),
			callbackFn: async ids => {
				if (!groupData) {
					return;
				}

				waitUserResponseToGroupBatch.current?.open({
					title: t('general.change_status'),
					content: <ChangeStatusModal contactIDs={ids} ref={waitUserResponseToGroupBatch} />,
					isConfirmButtonVisible: false,
					isBackButtonVisible: false,
				});
			},
		},
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids, table) => {
				const payload = {
					contacts: ids.map(id => {
						return {
							contactID: parseInt(id),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_contacts'),
					content: t('general.really_remove_contacts'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});

				if (response === true) {
					deleteMultipleContacts(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['contacts'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	const selectColumns = useRef<ConfirmModalHandle>(null);

	function openExportModal(): void {
		if (!customFieldData || !currentAccount) {
			return;
		}

		selectColumns.current?.open({
			title: t('general.export'),
			content: <ExportModal ref={selectColumns} currentAccount={currentAccount} customFields={customFieldData?.data.customFields} useDataCollection={useDataCollection} />,
			isConfirmButtonVisible: false,
			isBackButtonVisible: false,
		});
	}

	async function selectAllFn(parsedApiFilters: { relatedFilters?: string; filters?: string } | null): Promise<number[]> {
		let params: {
			selectColumns: string;
			filters?: string;
			relatedFilters?: string;
		} = {
			selectColumns: 'contactID',
		};

		if (parsedApiFilters?.filters) {
			params = {
				...params,
				filters: parsedApiFilters?.filters,
			};
		}

		if (parsedApiFilters?.relatedFilters) {
			params = {
				...params,
				relatedFilters: parsedApiFilters?.relatedFilters,
			};
		}
		const response = await contactsService.getAllResources(params);

		return response.data.contacts.map(contact => contact.contactID);
	}

	return (
		<Card.Root flex={1} display="flex">
			<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile || window.innerHeight < 930 ? 'initial' : 'auto'}>
				<DataCollectionContextProvider
					dataKey="contacts"
					keyAttribute="contactID"
					emptyDataText={t('general.no_contacts_found')}
					emptyDataIcon={IoBookOutline}
					fetchFn={(...args) => ContactsService.getAllResources(...args)}
					queryParams={{ getRelated: 'contactGroups,contactCustomFields', scopeWith: 'timeLatestMessageSent' }}
					initialItemsPerPage={25}
					columns={columns.filter(column => column !== undefined)}
					defaultHiddenColumns={['postalCode', 'contactID']}
					isServerSide={true}
					selectedItemsActions={selectedItemsActions}
					defaultSort={{ id: 'timeCreated', desc: true }}
					isLoading={isGroupsPending}
					selectAllFn={parsedApiFilters => selectAllFn(parsedApiFilters)}
					onClickRow={row => {
						openDrawer({
							id: 'contactDrawer',
							content: <ContactDrawer closeDrawer={() => closeDrawer('contactDrawer')} contactID={row.original.contactID} />,
						});
					}}
				>
					<Flex gap={2} flexWrap="wrap" flexShrink={0}>
						<DataCollectionTableHeader />
						<ExportButton useDataCollection={useDataCollection} openExportModal={openExportModal} />
						<Button
							data-cy="addContactButton"
							minW="175px"
							alignSelf="start"
							onClick={() =>
								openDrawer({
									id: 'addContactDrawer',
									content: <ContactDrawer closeDrawer={() => closeDrawer('addContactDrawer')} />,
								})
							}
						>
							{t('contacts.add_contact')}
						</Button>
					</Flex>

					<Box flex={1} overflow="auto" minH={0}>
						<DataCollectionTable />
					</Box>

					<Flex gap={2} flexShrink={0}>
						<DataCollectionTableFooter />
						<ConfirmModal ref={confirmRemoveRef} />
						<ConfirmModal ref={waitUserResponseToGroupBatch} />
						<ConfirmModal ref={selectColumns} />
					</Flex>
				</DataCollectionContextProvider>
			</Card.Body>
		</Card.Root>
	);
};

function ExportButton({ openExportModal, useDataCollection }: { openExportModal: () => void; useDataCollection: () => DataCollectionContextProps<Contact> }) {
	const exportingCount = Object.keys(useDataCollection().selectedRows).length;

	const { t } = useTranslation();
	return <Button onClick={openExportModal}>{exportingCount > 0 ? t('general.export_count', { count: exportingCount }) : t('general.export')}</Button>;
}

export function ExportModal({
	ref,
	currentAccount,
	customFields,
	useDataCollection,
}: {
	ref: RefObject<ConfirmModalHandle | null>;
	currentAccount: Account;
	customFields: CustomField[];
	useDataCollection: () => DataCollectionContextProps<Contact>;
}) {
	const { t } = useTranslation();
	const [columns, setColumns] = useState<string[]>(['firstName', 'lastName', 'phoneNumber']);

	const rows = useDataCollection().rows;
	const filters = useDataCollection().parsedApiFilters?.filters;
	const relatedFilters = useDataCollection().parsedApiFilters?.relatedFilters;
	const search = useDataCollection().search;
	const queryParams = useDataCollection().queryParams;
	const exportingCount = Object.keys(useDataCollection().selectedRows).length;
	const selectedIds = Object.keys(useDataCollection().selectedRows);

	function getFilters(): string {
		const params: string[] = [];

		if (exportingCount > 0) {
			const filter = `filters=contactID:in:${selectedIds.join(',')}`;
			params.push(filter);

			return `?${params.join('&')}`;
		}

		if (filters?.length) {
			params.push(`filters=${filters}`);
		}

		if (relatedFilters?.length || queryParams?.relatedFilters?.length) {
			params.push(`relatedFilters=${relatedFilters || queryParams?.relatedFilters}`);
		}

		if (search?.length) {
			params.push(`search=${search}`);
		}

		return params.length ? `?${params.join('&')}` : '';
	}

	async function handleExport(): Promise<void> {
		try {
			const response = await contactsService.export({
				filters: getFilters(),
				columnsToExport: columns.map(column => (column === 'phoneNumber' ? 'phone' : column)),
			});

			const now = DateTime.now();
			const formattedNow = now.toFormat('yyyy-MM-dd');
			const filename = `${currentAccount?.name}-contacts-export-${formattedNow}.xlsx`.toLowerCase().replace(/\s+/g, '-');

			const blob = new Blob([response.data], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});

			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
			ref.current?.close();
		} catch (error) {
			toaster.create({
				type: 'error',
				title: parseErrorToString(error),
			});
		}
	}

	const exportableColumns = useMemo(
		() => [
			{ key: 'firstName', label: t('general.first_name') },
			{ key: 'lastName', label: t('general.last_name') },
			{ key: 'email', label: t('general.email') },
			{ key: 'phoneNumber', label: t('general.phone') },
			{ key: 'company', label: t('general.company') },
			{ key: 'jobTitle', label: t('general.job_title') },
			{ key: 'postalAddress', label: t('general.postal_address') },
			{ key: 'postalCode', label: t('general.postal_code') },
			{ key: 'city', label: t('general.city') },
			{ key: 'description', label: t('general.description') },
			{ key: 'status', label: t('general.status') },
			{ key: 'createdFrom', label: t('general.created_from') },
			{ key: 'groupNames', label: t('general.groups') },
			{ key: 'timeCreated', label: t('general.time_created') },
			{ key: 'timeModified', label: t('general.time_modified') },

			...customFields.map(customField => {
				return {
					key: `customField-${customField.customFieldID}`,
					label: customField.label,
				};
			}),
		],
		[t, customFields],
	);

	return (
		<Flex flexDirection={'column'}>
			{exportingCount > 0 ? <Span mb="3">{t('general.exporting_rows', { count: exportingCount })}</Span> : null}
			<Heading size="sm">{t('general.select_columns_to_export')}</Heading>

			<Flex flexDirection={'column'} gap={2} marginTop={4} marginBottom={2}>
				<Checkbox.Root
					colorPalette="brand.primary"
					checked={columns.length === exportableColumns.length}
					onCheckedChange={({ checked }) => {
						if (checked) {
							setColumns(exportableColumns.map(col => col.key));
						} else {
							setColumns([]);
						}
					}}
				>
					<Checkbox.HiddenInput />
					<Checkbox.Control />
					<Checkbox.Label>{t('general.select_all')}</Checkbox.Label>
				</Checkbox.Root>
				<hr />

				{exportableColumns.map(({ key, label }) => (
					<Checkbox.Root
						key={key}
						colorPalette={'brand.primary'}
						checked={columns.includes(key)}
						onCheckedChange={({ checked }) => {
							if (checked && !columns.includes(key)) {
								setColumns(prev => [...prev, key]);
							} else if (!checked) {
								setColumns(prev => prev.filter(col => col !== key));
							}
						}}
					>
						<Checkbox.HiddenInput />
						<Checkbox.Control id={key} />
						<Checkbox.Label textAlign="left">{label}</Checkbox.Label>
					</Checkbox.Root>
				))}
			</Flex>
			{rows.length === 0 ? (
				<Text mt={4} color="red.500">
					{t('general.nothing_to_export')}
				</Text>
			) : null}
			<Flex gap={2} marginTop={4}>
				<Button variant="outline" onClick={() => ref.current?.close()}>
					{t('general.cancel')}
				</Button>
				<Button disabled={columns.length === 0 || rows.length === 0} onClick={handleExport}>
					{t('general.export')}
				</Button>
			</Flex>
		</Flex>
	);
}
